import React, { ReactNode } from 'react';
import { useField, FieldHookConfig } from 'formik';
import { Box, Text, Radio, RadioGroup, Stack } from '@tonic-ui/react';

import { FieldTextLabel, InlineError } from './index';

type RadioButtonGroupProps = FieldHookConfig<string | number | boolean | ReactNode> & {
  radioOptions: { label: string; value: string; disabled?: boolean }[];
  label?: string;
  size?: string;
  isRequired?: boolean;
  onChangeSelection?: (value: string) => void;
};

const RadioButtonGroup = ({
  radioOptions = [],
  label = '',
  size = 'sm',
  isRequired = false,
  onChangeSelection,
  disabled = false,
  ...rest
}: RadioButtonGroupProps): JSX.Element => {
  const [field, meta, helpers] = useField(rest);
  const fieldError = meta.error && meta.touched ? meta.error : '';
  const { setValue } = helpers;

  return (
    <>
      {label !== '' && (
        <Box marginaBottom="3x">
          <FieldTextLabel name={label} htmlFor={rest?.name} isRequired={isRequired} />
        </Box>
      )}
      <RadioGroup
        disabled={disabled}
        value={field.value}
        onChange={(value) => {
          setValue(value);
          if (onChangeSelection) onChangeSelection(value);
        }}
      >
        <Stack spacing="3x">
          {radioOptions.map((radioOption) => (
            <Radio
              key={radioOption.value}
              size={size}
              value={radioOption.value}
              disabled={radioOption.disabled}
            >
              {radioOption.label}
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
      {fieldError && (
        <Text size="xs" mt="1x">
          <InlineError errorName={fieldError} />
        </Text>
      )}
    </>
  );
};

RadioButtonGroup.displayName = 'RadioButtonGroup';

export default RadioButtonGroup;
